//#region src/tenants/hhb/roster.ts
/**
* HHB's own clinicians — the authority for which `providers` rows carry HHB's
* `tenant_id`, and so for which PCPs the eligibility check calls "ours".
*
* Source: the "Health Hub Physicians, Inc. Providers" roster (every row shares
* EIN 833612007), narrowed to the nine individuals HHB confirmed on 2026-09-11.
*
* Duplicate this file's shape into other tenant folders rather than importing it,
* the same way `clinic.ts` and `routing.ts` are duplicated — Skylight must be able
* to gain a roster without editing HHB.
*
* `providerName` is reconciled onto existing rows, so it is the authority for
* identity and not only for ownership. Keep it "First M Last": two parsers read
* `providers.provider_name`, and a stored "Cantu, Rachel" fills Availity's
* prior-auth form with first name "Cantu" while a trailing ", NP" makes the
* eligibility job search Availity for a provider called "NP".
*
* `location` is the roster's *Physical* address, not its Billing one. Every
* individual bills to 2450 Holcombe Blvd in Houston, so billing addresses would
* collapse eight of the nine onto one location and tell a payer the PCP practises
* somewhere they do not. Availity prior auth requires the patient's PCP to have
* at least one location and one specialty and throws otherwise, so a clinician
* without them cannot be a PCP on a prior-authorised referral — and
* `setHhbPerLocationPcp` will not point a clinic at them.
*
* `specialty` must already exist in the `specialties` table; the sync refuses a
* name it cannot find rather than inventing a row in a shared taxonomy. The
* roster's taxonomy codes are recorded per entry where they disagree with what we
* can declare. See specs/hhb-per-location-pcp.md.
*
* `faxNumber` is not declared on any entry below, and prior auth needs one: BCBSTX
* requires a Contact Fax from the requesting provider. Adding it here only helps
* for an address the directory does not already hold, because the sync writes a
* declared fax on insert and reuses a matching `locations` row untouched — see
* docs/provider-routing.md. For an address that already exists, correct the fax on
* the location itself.
*/
const hhbProviderRoster = [
	{
		npi: "1477784072",
		providerName: "Maritza R Armendariz",
		location: {
			address: "602 W OReilly St, Suite C",
			city: "Presidio",
			state: "TX",
			zip: "79845"
		},
		specialty: "Nurse Practitioner"
	},
	{
		npi: "1205787504",
		providerName: "Rachel Lee Cantu",
		location: {
			address: "105 E Oak St",
			city: "Marfa",
			state: "TX",
			zip: "79843",
			phoneNumber: "432-729-3000"
		},
		specialty: "Nurse Practitioner"
	},
	{
		npi: "1457956286",
		providerName: "Mary Claire Carbonell",
		location: {
			address: "333 Clay St #3300",
			city: "Houston",
			state: "TX",
			zip: "77002",
			phoneNumber: "832-662-2189"
		},
		specialty: "Nurse Practitioner"
	},
	{
		npi: "1306949805",
		providerName: "Donald Culbertson",
		location: {
			address: "105 E Oak St",
			city: "Marfa",
			state: "TX",
			zip: "79843",
			phoneNumber: "432-729-3000"
		},
		specialty: "Physician Assistant"
	},
	{
		npi: "1003806431",
		providerName: "Glenn R Davis",
		location: {
			address: "2255 E Mossy Oaks Rd Ste 680",
			city: "Spring",
			state: "TX",
			zip: "77389",
			phoneNumber: "281-537-0300"
		},
		specialty: "Internal Medicine"
	},
	{
		npi: "1992148076",
		providerName: "Ashish Gupta",
		location: {
			address: "333 Clay St #3300",
			city: "Houston",
			state: "TX",
			zip: "77002",
			phoneNumber: "832-662-2189"
		},
		specialty: "Internal Medicine"
	},
	{
		npi: "1659856680",
		providerName: "Rachel Menges",
		location: {
			address: "333 Clay St #3300",
			city: "Houston",
			state: "TX",
			zip: "77002",
			phoneNumber: "832-662-2189"
		},
		specialty: "Nurse Practitioner"
	},
	{
		npi: "1366713745",
		providerName: "Danielle Olszeski",
		location: {
			address: "2450 Holcombe Blvd # 240",
			city: "Houston",
			state: "TX",
			zip: "77021",
			phoneNumber: "832-662-2189"
		},
		specialty: "Physician Assistant"
	},
	{
		npi: "1134480262",
		providerName: "Sean Yarbrough",
		location: {
			address: "333 Clay St #3300",
			city: "Houston",
			state: "TX",
			zip: "77002",
			phoneNumber: "832-662-2189"
		}
	}
];

//#endregion
export { hhbProviderRoster };